import { useState, useEffect } from "react";
import { useNavigate, useLocation, useSearchParams, Link } from "react-router-dom";
import toast from "react-hot-toast";
import { FiUser, FiLock, FiEye, FiEyeOff, FiHome, FiShield, FiArrowLeft, FiAlertCircle } from "react-icons/fi";
import { useApp } from "../context/AppContext";
import api from "../services/api";

const ROLE_HOME = {
    superadmin: "/superadmin",
    admin: "/dashboard",
    teacher: "/teacher",
    student: "/student",
    parent: "/parent",
    accountant: "/accountant",
    librarian: "/librarian",
    warden: "/warden",
    clerk: "/clerk",
    storekeeper: "/storekeeper",
};

export default function Login() {
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams] = useSearchParams();
    const { loginSuccess } = useApp();

    const collegeParam = searchParams.get("college") || "";

    const [collegeInfo, setCollegeInfo] = useState(null);
    const [availableColleges, setAvailableColleges] = useState([]);
    const [loadingCollege, setLoadingCollege] = useState(Boolean(collegeParam));

    const [username, setUsername] = useState("");
    const [password, setPassword] = useState("");
    const [showPw, setShowPw] = useState(false);
    const [loading, setLoading] = useState(false);

    // Fetch college branding if collegeParam is present
    useEffect(() => {
        if (!collegeParam) {
            // Load list of colleges in case user landed on /login without college query
            api.get("/colleges/public")
                .then((res) => {
                    const list = res.data?.data || [];
                    setAvailableColleges(Array.isArray(list) ? list : []);
                })
                .catch(() => {});
            return;
        }

        setLoadingCollege(true);
        api.get("/colleges/public", { params: { code: collegeParam } })
            .then((res) => {
                const list = res.data?.data || [];
                if (list.length > 0) {
                    setCollegeInfo(list[0]);
                } else {
                    setCollegeInfo(null);
                }
            })
            .catch(() => {
                setCollegeInfo(null);
            })
            .finally(() => {
                setLoadingCollege(false);
            });
    }, [collegeParam]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!username.trim() || !password) {
            toast.error("Username and password are required.");
            return;
        }

        setLoading(true);
        try {
            const payload = {
                username: username.trim().toLowerCase(),
                password,
            };

            if (collegeParam) {
                payload.college = collegeParam;
            }

            const res = await api.post("/auth/login", payload);
            const { token, user } = res.data;

            loginSuccess(token, user);

            // If first login — go to change-password page regardless of role
            if (user.mustChangePassword) {
                navigate("/change-password", { replace: true });
                return;
            }

            // Otherwise go to role's home page (or wherever they were trying to go)
            const from = location.state?.from?.pathname;
            const home = ROLE_HOME[user.role] || "/dashboard";
            const isGenericPath = !from || from === "/" || from === "/dashboard" || from === "/login";
            navigate(isGenericPath ? home : from, { replace: true });
        } catch (err) {
            const msg = err.response?.data?.message || "Login failed.";
            toast.error(msg, { duration: 5000 });
        } finally {
            setLoading(false);
        }
    };

    return (
        <div style={{
            minHeight: "100vh",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: "linear-gradient(135deg, #f8fafc 0%, #edf2f7 100%)",
            padding: "1.5rem 1rem",
            fontFamily: "Inter, -apple-system, sans-serif"
        }}>
            <div style={{ width: "100%", maxWidth: 440 }}>

                {/* College Branding & Header */}
                <div style={{ textAlign: "center", marginBottom: "1.5rem" }}>
                    {collegeInfo?.logo?.url ? (
                        <img
                            src={collegeInfo.logo.url}
                            alt={collegeInfo.name}
                            style={{
                                width: 68,
                                height: 68,
                                objectFit: "contain",
                                borderRadius: 12,
                                background: "#ffffff",
                                padding: 6,
                                boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                                margin: "0 auto 0.75rem",
                                display: "block"
                            }}
                        />
                    ) : (
                        <div style={{
                            width: 60,
                            height: 60,
                            borderRadius: 16,
                            background: "linear-gradient(135deg, #003b67 0%, #0284c7 100%)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            margin: "0 auto 0.75rem",
                            boxShadow: "0 6px 16px rgba(0,59,103,0.25)",
                            color: "#fff"
                        }}>
                            <FiHome size={28} />
                        </div>
                    )}

                    {collegeInfo ? (
                        <>
                            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 6, marginBottom: 6 }}>
                                <span style={{
                                    fontSize: 11,
                                    fontWeight: 700,
                                    textTransform: "uppercase",
                                    letterSpacing: "0.06em",
                                    background: "#e0f2fe",
                                    color: "#0369a1",
                                    padding: "2px 8px",
                                    borderRadius: 99
                                }}>
                                    Campus Code: {collegeInfo.code}
                                </span>
                                {collegeInfo.discipline && (
                                    <span style={{
                                        fontSize: 11,
                                        fontWeight: 600,
                                        background: "#f1f5f9",
                                        color: "#475569",
                                        padding: "2px 8px",
                                        borderRadius: 99
                                    }}>
                                        {collegeInfo.discipline}
                                    </span>
                                )}
                            </div>
                            <h1 style={{ fontSize: 19, fontWeight: 800, color: "#0f172a", marginBottom: 4, lineHeight: 1.35 }}>
                                {collegeInfo.name}
                            </h1>
                            <p style={{ fontSize: 12, color: "#64748b", margin: 0 }}>
                                {collegeInfo.sansthaName || "Central India Group of Institutions"}
                            </p>
                        </>
                    ) : (
                        <>
                            <h1 style={{ fontSize: 20, fontWeight: 800, color: "#0f172a", marginBottom: 4 }}>
                                Central India Group of Institutions
                            </h1>
                            <p style={{ fontSize: 13, color: "#64748b" }}>
                                College Management System &amp; Portal
                            </p>
                        </>
                    )}
                </div>

                {/* Card */}
                <div className="card" style={{
                    padding: "2rem",
                    borderRadius: 14,
                    boxShadow: "0 10px 25px rgba(0,0,0,0.05)",
                    border: "1px solid #e2e8f0",
                    background: "#ffffff"
                }}>
                    {collegeInfo ? (
                        <div style={{
                            padding: "0.6rem 0.85rem",
                            background: "#f8fafc",
                            border: "1px solid #e2e8f0",
                            borderRadius: 8,
                            marginBottom: "1.25rem",
                            fontSize: 12,
                            color: "#475569",
                            display: "flex",
                            alignItems: "center",
                            gap: 8
                        }}>
                            <FiAlertCircle size={15} color="#0284c7" style={{ flexShrink: 0 }} />
                            <span>
                                Sign-in is restricted to students, faculty, and administrative staff of <strong>{collegeInfo.code}</strong>.
                            </span>
                        </div>
                    ) : availableColleges.length > 0 ? (
                        <div style={{ marginBottom: "1.25rem" }}>
                            <label className="form-label" style={{ fontSize: 12, fontWeight: 700 }}>
                                Select Your College Campus
                            </label>
                            <select
                                className="form-control"
                                onChange={(e) => {
                                    if (e.target.value) {
                                        navigate(`/login?college=${e.target.value}`);
                                    }
                                }}
                                defaultValue=""
                                style={{ fontSize: 13, fontWeight: 600, color: "#003b67" }}
                            >
                                <option value="" disabled>Choose your college to continue…</option>
                                {availableColleges.map((c) => (
                                    <option key={c._id} value={c.code}>
                                        {c.name} ({c.code})
                                    </option>
                                ))}
                            </select>
                        </div>
                    ) : null}

                    <form onSubmit={handleSubmit}>
                        {/* Username */}
                        <div className="form-group" style={{ marginBottom: "1.1rem" }}>
                            <label className="form-label" style={{ fontSize: 12, fontWeight: 700 }}>
                                Username / ID
                            </label>
                            <div style={{ position: "relative" }}>
                                <FiUser size={15} style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", color: "var(--text-muted)" }} />
                                <input
                                    className="form-control"
                                    style={{ paddingLeft: 36 }}
                                    placeholder="Enter your username"
                                    value={username}
                                    onChange={(e) => setUsername(e.target.value)}
                                    autoFocus
                                    autoComplete="username"
                                />
                            </div>
                            <span className="form-hint" style={{ fontSize: 11, color: "#94a3b8", marginTop: 4, display: "block" }}>
                                Students: enrollment no. · Staff: assigned username
                            </span>
                        </div>

                        {/* Password */}
                        <div className="form-group" style={{ marginBottom: "1.5rem" }}>
                            <label className="form-label" style={{ fontSize: 12, fontWeight: 700 }}>
                                Password
                            </label>
                            <div style={{ position: "relative" }}>
                                <FiLock size={15} style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", color: "var(--text-muted)" }} />
                                <input
                                    type={showPw ? "text" : "password"}
                                    className="form-control"
                                    style={{ paddingLeft: 36, paddingRight: 40 }}
                                    placeholder="Enter your password"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    autoComplete="current-password"
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPw((p) => !p)}
                                    style={{
                                        position: "absolute", right: 10, top: "50%",
                                        transform: "translateY(-50%)",
                                        background: "none", border: "none",
                                        cursor: "pointer", color: "var(--text-muted)",
                                        padding: 4,
                                    }}
                                    tabIndex={-1}
                                >
                                    {showPw ? <FiEyeOff size={15} /> : <FiEye size={15} />}
                                </button>
                            </div>
                        </div>

                        <button
                            type="submit"
                            className="btn btn-primary"
                            style={{
                                width: "100%",
                                justifyContent: "center",
                                padding: "0.7rem",
                                fontSize: 14,
                                fontWeight: 700,
                                background: "#003b67",
                                borderRadius: 8
                            }}
                            disabled={loading || loadingCollege}
                        >
                            {loading ? "Verifying Credentials…" : "Sign In to Portal"}
                        </button>
                    </form>

                    {/* Switch College link */}
                    <div style={{ textAlign: "center", marginTop: "1.25rem", borderTop: "1px solid #f1f5f9", paddingTop: "1rem" }}>
                        <Link
                            to="/"
                            style={{
                                color: "#003b67",
                                fontSize: 12.5,
                                fontWeight: 600,
                                textDecoration: "none",
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 4
                            }}
                        >
                            <FiArrowLeft size={13} /> Select a different college
                        </Link>
                    </div>
                </div>

                {/* Non-Teaching Staff Leave Link */}
                <div style={{
                    marginTop: "1.25rem",
                    padding: "0.85rem 1rem",
                    background: "#ffffff",
                    border: "1px solid #e2e8f0",
                    borderRadius: 10,
                    textAlign: "center",
                    boxShadow: "0 1px 3px rgba(0,0,0,0.03)"
                }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: "#475569", marginBottom: 4 }}>
                        Non-Teaching Staff Member?
                    </div>
                    <button
                        type="button"
                        onClick={() => navigate("/staff/leave-apply")}
                        style={{
                            background: "none",
                            border: "none",
                            color: "#003b67",
                            fontWeight: 700,
                            fontSize: 12.5,
                            cursor: "pointer",
                            padding: 0,
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 4,
                        }}
                    >
                        Apply for Leave via OTP (No Login Required) &rarr;
                    </button>
                </div>

                {/* Discreet Super Admin Login Link */}
                <div style={{ textAlign: "center", marginTop: "1.5rem" }}>
                    <Link
                        to="/superadmin/login"
                        style={{
                            color: "#94a3b8",
                            fontSize: 12,
                            textDecoration: "none",
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 5,
                            transition: "color 0.15s"
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.color = "#003b67"}
                        onMouseLeave={(e) => e.currentTarget.style.color = "#94a3b8"}
                    >
                        <FiShield size={13} /> Super Administrator? Sign in to Executive Cockpit &rarr;
                    </Link>
                </div>
            </div>
        </div>
    );
}